




































































































































































































import { Component, Ref, Vue } from "vue-property-decorator";
import MenuIconWithSelectionListComponent from "@/components/common/FilterMenu.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import MiraExceptionDialogComponent from "@/components/admin/mira/MiraExceptionDialog.vue";
import MiraExceptionSuggestionDismissDialogComponent from "@/components/admin/mira/MiraExceptionSuggestionDismissDialog.vue";
import { ClientAdministrationRow } from "@/data/models/ClientAdministrationRow";
import { MiraException, MiraExceptionSuggestion } from "@/data/models/MiraExceptionAdministration";
import { listClientsForAdministration } from "@/data/repository/ClientAdministrationRepository";
import { listMiraExceptions, listMiraExceptionSuggestions } from "@/data/repository/MiraExceptionAdministrationRepository";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import { formatFloat } from "@/utils/LocalisationHelper";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import { COLOR_ALL_DEVICES_EDIT_ICON, COLOR_LIST_ROW_HOVER_DARK, COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { getValue, setValue } from "@/utils/SecureLocalStorage";
import { formatNumber, getLastVisitedItem, LAST_VISITED_ITEM_BACKGROUND_STYLE, setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import { clientLabel } from "@/utils/MiraCompanyAdministration";
import {
  dateOfDay,
  filterExceptions,
  filterSuggestions,
  groupSuggestions,
  MIRA_EXCEPTION_FILTERS,
  MiraExceptionFilter,
  MiraSuggestionGroup,
  suggestionPrefill,
} from "@/utils/MiraExceptionAdministration";
import { themed } from "@/utils/Theme";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/**
 * Mira-Ausnahmen (Hans, Track #2610040 R8): a Mandant recorded an event late between two days (offer answers
 * typed in after the fact, old projects closed in a batch), so Mira leaves the durations built on that event
 * out and says how many on the tile. A row opens the entry to view and edit; the round plus adds one. An
 * edit reaches Mira with the next nightly run.
 *
 * Above the entries, Mira's suggestions (R8 Phase 3): the days on which a Mandant recorded many closings or
 * answers late, found overnight. ÜBERNEHMEN opens a new entry filled with them, VERWERFEN asks why and
 * hides the suggestion. A taken-over suggestion leaves the list at once; IRES Cloud drops it the next night.
 *
 * **Not the access boundary**: `listMiraExceptionsForAdministration` repeats the portal and
 * `MANAGE_CLIENTS` checks, as every save does.
 */
const DEFAULT_FILTER: MiraExceptionFilter = "ACTIVE";
/** Session storage key (see `SecureLocalStorage`) for the list's filter. */
const FILTER_STORAGE_KEY = "ADMIN_MIRA_EXCEPTIONS_FILTER";

@Component({
  components: {
    "menu-icon": MenuIconWithSelectionListComponent,
    "content-state": ContentStateComponent,
    "exception-dialog": MiraExceptionDialogComponent,
    "dismiss-dialog": MiraExceptionSuggestionDismissDialogComponent,
  },
})
export default class MiraExceptionListComponent extends Vue {
  @Ref("exceptionDialog")
  exceptionDialog!: MiraExceptionDialogComponent;

  @Ref("dismissDialog")
  dismissDialog!: MiraExceptionSuggestionDismissDialogComponent;

  exceptions: MiraException[] = [];
  suggestions: MiraExceptionSuggestion[] = [];
  /** The Mandanten whose suggestions are open; all collapsed at first. */
  expandedClientIds: string[] = [];
  clients: ClientAdministrationRow[] = [];
  filter: MiraExceptionFilter = DEFAULT_FILTER;
  search = "";
  isLoading = true;
  loadError = false;
  lastVisitedId: string | null = null;

  readonly filters = MIRA_EXCEPTION_FILTERS;
  readonly defaultFilter = DEFAULT_FILTER;

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  get clientsById(): Record<string, ClientAdministrationRow> {
    return Object.fromEntries(this.clients.map((client) => [client.id, client]));
  }

  get rows(): MiraException[] {
    return filterExceptions(this.exceptions, this.filter, this.search, (clientId) => this.labelOf(clientId));
  }

  get suggestionRows(): MiraExceptionSuggestion[] {
    return filterSuggestions(this.suggestions, this.search, (clientId) => this.labelOf(clientId));
  }

  get showSuggestions(): boolean {
    return !this.isLoading && !this.loadError && this.suggestionRows.length > 0;
  }

  /** The suggestions by Mandant, each group newest days first. */
  get suggestionGroups(): MiraSuggestionGroup[] {
    return groupSuggestions(this.suggestionRows, (clientId) => this.labelOf(clientId));
  }

  /**
   * The entries' column headings in the sticky card. Not while the suggestions are shown: those sit
   * between the card and the entries, with their own table headings, and "Ausnahmen" names the entries.
   */
  get hasColumns(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0 && !this.showSuggestions;
  }

  get rowIconColor(): string {
    return COLOR_ALL_DEVICES_EDIT_ICON;
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  labelOf(clientId: string): string {
    return clientLabel(this.clientsById[clientId], clientId);
  }

  /** "16.12.2024 – 24.02.2025" in the locale's date form. */
  rangeText(row: { from: string; to: string }): string {
    return `${this.dayText(row.from)} – ${this.dayText(row.to)}`;
  }

  /** A suggestion's days: one date for a single day (most Projektabschluss suggestions), else the range. */
  daysText(suggestion: MiraExceptionSuggestion): string {
    return suggestion.from === suggestion.to ? this.dayText(suggestion.from) : this.rangeText(suggestion);
  }

  private dayText(value: string): string {
    const date = dateOfDay(value);
    return date ? formatDateToDayMonthYear(date) ?? value : value;
  }

  formatNumber(value: number): string {
    return formatNumber(value);
  }

  /** P1 in a cell: "49 → 45,2 Tage", the mean or median with and without the late projects. */
  changeText(suggestion: MiraExceptionSuggestion, measure: "mean" | "median"): string {
    const figure = suggestion.figure;
    if (!figure) return "–";
    const [withLate, withoutLate] = measure === "mean" ? [figure.meanWith, figure.meanWithout] : [figure.medianWith, figure.medianWithout];
    return this.$t("admin.miraExceptions.suggestions.change", [this.days(withLate), this.days(withoutLate)]).toString();
  }

  /** P1 in full, the cells' tooltip: "Projektdauer 2026: Ø 49 Tage, ohne diese Abschlüsse 45,2 (Median 33 → 31)". */
  figureText(suggestion: MiraExceptionSuggestion): string {
    const figure = suggestion.figure;
    if (!figure) return "";
    return this.$t(`admin.miraExceptions.suggestions.figure.${suggestion.event}`, [
      String(figure.year),
      this.days(figure.meanWith),
      this.days(figure.meanWithout),
      this.days(figure.medianWith),
      this.days(figure.medianWithout),
    ]).toString();
  }

  private days(value: number | null): string {
    return value === null ? "–" : formatFloat(value, 1);
  }

  isExpanded(clientId: string): boolean {
    return this.expandedClientIds.includes(clientId);
  }

  toggleGroup(clientId: string) {
    this.expandedClientIds = this.isExpanded(clientId)
      ? this.expandedClientIds.filter((id) => id !== clientId)
      : [...this.expandedClientIds, clientId];
  }

  /** The dark-mode hover of a group card, where a shadow is invisible (ui-conventions.md). */
  hoverStyle(hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    return background ? `background-color: ${background}` : "";
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(row: MiraException, hover: boolean): string {
    const background = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";
    if (background) {
      return `background-color: ${background}`;
    }
    return row.id === this.lastVisitedId ? LAST_VISITED_ITEM_BACKGROUND_STYLE : "";
  }

  filterName(filter: MiraExceptionFilter): string {
    return this.$t(`admin.miraExceptions.filters.${filter}`).toString();
  }

  onFilterChanged(filter: MiraExceptionFilter) {
    this.filter = filter;
    setValue(FILTER_STORAGE_KEY, filter);
  }

  onSearch(value: string | null) {
    this.search = value ?? "";
  }

  async open(row: MiraException | null) {
    if (row) {
      this.lastVisitedId = row.id;
      setLastVisitedItem(row.id, StorageKeyLastVisitedItemLevel.LEVEL_1);
    }
    const result = await this.exceptionDialog.show(row, this.clients);
    if (result === "CHANGED_MEANWHILE") {
      this.refreshEntries();
    } else if (result) {
      this.lastVisitedId = result.id;
      setLastVisitedItem(result.id, StorageKeyLastVisitedItemLevel.LEVEL_1);
      const index = this.exceptions.findIndex((entry) => entry.id === result.id);
      this.exceptions = index === -1 ? [...this.exceptions, result] : this.exceptions.map((entry) => (entry.id === result.id ? result : entry));
    }
  }

  /** ÜBERNEHMEN: a new entry filled with the suggestion; once saved, it joins the list and the suggestion goes. */
  async takeOver(suggestion: MiraExceptionSuggestion) {
    const result = await this.exceptionDialog.show(null, this.clients, suggestionPrefill(suggestion));
    if (result && result !== "CHANGED_MEANWHILE") {
      this.lastVisitedId = result.id;
      setLastVisitedItem(result.id, StorageKeyLastVisitedItemLevel.LEVEL_1);
      this.exceptions = [...this.exceptions, result];
      this.suggestions = this.suggestions.filter((entry) => entry.id !== suggestion.id);
    }
  }

  /** VERWERFEN: asks why, then the suggestion goes; gone or changed overnight, the list is read again. */
  async dismiss(suggestion: MiraExceptionSuggestion) {
    const summary = [this.labelOf(suggestion.clientId), this.$t(`admin.miraExceptions.events.${suggestion.event}`).toString(), this.daysText(suggestion)].join(" · ");
    const result = await this.dismissDialog.show(suggestion, summary);
    if (result === "DISMISSED") {
      this.suggestions = this.suggestions.filter((entry) => entry.id !== suggestion.id);
    } else if (result === "RELOAD") {
      this.refreshEntries();
    }
  }

  /**
   * After a refusal (an entry changed meanwhile, a suggestion gone or changed overnight): reads the entries
   * and the suggestions again in the background, without the page's loading state, so the list stays where
   * it is (Taimoor, 2026-10-20: no full reload after a save). The Mandanten do not change here, so they are
   * not read again.
   */
  async refreshEntries() {
    try {
      const [exceptions, suggestions] = await Promise.all([
        listMiraExceptions(),
        // Swallowed on purpose, as in load(): the suggestions are an aid.
        listMiraExceptionSuggestions().catch(() => this.suggestions),
      ]);
      this.exceptions = exceptions;
      this.suggestions = suggestions;
    } catch {
      // Swallowed on purpose: the list keeps what it showed, and the refresh button reloads on demand.
    }
  }

  async load() {
    this.isLoading = true;
    this.loadError = false;
    try {
      const [exceptions, clients, suggestions] = await Promise.all([
        listMiraExceptions(),
        listClientsForAdministration(),
        // Swallowed on purpose: the suggestions are an aid, and a failure to list them (an IRES Cloud
        // without the callable yet) must not hide the entries themselves.
        listMiraExceptionSuggestions().catch(() => [] as MiraExceptionSuggestion[]),
      ]);
      this.exceptions = exceptions;
      this.clients = clients.clients;
      this.suggestions = suggestions;
    } catch {
      // Swallowed on purpose: the screen shows its own failure state, and the refresh button retries.
      this.loadError = true;
    } finally {
      this.isLoading = false;
    }
  }

  mounted() {
    ToolbarContentModule.setI18nResource("admin.miraExceptions.title");
    const stored = getValue(FILTER_STORAGE_KEY);
    this.filter = MIRA_EXCEPTION_FILTERS.find((filter) => filter === stored) ?? DEFAULT_FILTER;
    this.lastVisitedId = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.load();
  }
}
